import type { ImpromptuLanguageCode } from "./config";

export type ImpromptuLanguagePage = {
  code: ImpromptuLanguageCode;
  slug: string;
  name: string;
  shortName: string;
  path: string;
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  heading: string;
  lead: string;
  storyHeading: string;
  storyBody: string;
  scenarios: ReadonlyArray<readonly [string, string]>;
  focusHeading: string;
  focusLead: string;
  focusPoints: ReadonlyArray<readonly [string, string]>;
  faqs: ReadonlyArray<readonly [string, string]>;
};

const profiles: ReadonlyArray<Omit<ImpromptuLanguagePage, "path">> = [
  {
    code: "en",
    slug: "english",
    name: "English",
    shortName: "English",
    metaTitle: "Impromptu English Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Answer 32 impromptu English speaking topics and get a transcript, percentage scores, delivery feedback and pronunciation assessment.",
    eyebrow: "Impromptu English speaking practice",
    heading: "Find the English words when the question changes.",
    lead: "Pick an unfamiliar topic, answer in one take, and see whether your English stayed clear when you had no script to follow.",
    storyHeading: "Fluent rehearsals are not the same as spontaneous English.",
    storyBody:
      "The difficult moment is often the follow-up: explaining a decision in a meeting, adding detail in an interview, or keeping a conversation moving after the easy phrases run out.",
    scenarios: [
      ["Meetings", "Give a view, support it, and close without circling back."],
      ["Interviews", "Build an answer to the follow-up you did not rehearse."],
      ["Exams", "Turn a broad prompt into a structured spoken response."],
      ["Everyday talk", "Keep speaking when the conversation moves somewhere new."],
    ],
    focusHeading: "Practise the English between the prepared lines.",
    focusLead:
      "Use each take to make one practical change, then answer again while the wording is still fresh.",
    focusPoints: [
      ["State the point early", "Open with your answer before adding reasons or examples."],
      [
        "Connect the ideas",
        "Use natural links so the answer sounds organised rather than memorised.",
      ],
      ["Finish deliberately", "End on the point you want the listener to remember."],
    ],
    faqs: [
      [
        "Can I use these topics for an English speaking exam?",
        "Yes. The opinion, story, explanation and persuasion prompts are useful for practising timed, unrehearsed answers, although ChickyTutor does not predict an official exam grade.",
      ],
      [
        "Is this only for English learners?",
        "No. Fluent and native speakers can use the prompts to practise clearer unscripted answers; pronunciation assessment is reserved for learner levels.",
      ],
    ],
  },
  {
    code: "es",
    slug: "spanish",
    name: "Spanish",
    shortName: "Spanish",
    metaTitle: "Impromptu Spanish Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Practise 32 Spanish speaking topics without a script. Record one take and get transcript-based scores, coaching and pronunciation feedback.",
    eyebrow: "Impromptu Spanish speaking practice",
    heading: "Keep your Spanish going beyond the first sentence.",
    lead: "Choose a Spanish prompt, speak for up to 90 seconds, and find the places where your message, grammar or delivery lost momentum.",
    storyHeading: "Real Spanish rarely waits for a perfect sentence.",
    storyBody:
      "A colleague asks why. A host changes the subject. An examiner wants an example. Impromptu practice trains the part after your familiar opening line.",
    scenarios: [
      ["Travel", "Explain a preference or solve a small problem without switching language."],
      ["Work", "Respond to a question and make your reason easy to follow."],
      ["Speaking exams", "Develop a position with an example and a clear ending."],
      ["Conversation", "Move from short replies to connected Spanish."],
    ],
    focusHeading: "Build a Spanish answer, not a translated sentence.",
    focusLead:
      "The report helps you notice what happens when you create the message directly in Spanish.",
    focusPoints: [
      [
        "Choose the time frame",
        "Keep the verb tense consistent when a story moves from context to action.",
      ],
      ["Link the reason", "Connect opinions with a reason and a concrete example."],
      [
        "Recover smoothly",
        "Replace a missing word with a description instead of abandoning the thought.",
      ],
    ],
    faqs: [
      [
        "Is the practice based on Spain Spanish?",
        "The prompt library and pronunciation locale use Spanish from Spain. Speakers of other varieties can still practise, but regional wording and pronunciation may differ.",
      ],
      [
        "Can beginners use the Spanish prompts?",
        "Yes. Choose First steps or Beginner so the feedback judges the answer against an appropriate level.",
      ],
    ],
  },
  {
    code: "fr",
    slug: "french",
    name: "French",
    shortName: "French",
    metaTitle: "Impromptu French Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Speak on 32 impromptu French topics and receive a transcript, percentage scores, practical feedback and pronunciation assessment.",
    eyebrow: "Impromptu French speaking practice",
    heading: "Make your point in French before you over-edit it.",
    lead: "Answer an unexpected French question in one take. Then use the transcript and scorecard to make the next answer cleaner.",
    storyHeading: "French conversation does not come with subtitles or retakes.",
    storyBody:
      "You may know the grammar on paper and still hesitate when someone asks for an opinion, a story, or a quick explanation. This gives that moment somewhere to practise.",
    scenarios: [
      ["Conversation", "Add detail when someone asks a natural follow-up."],
      ["Work and study", "Explain a choice without building the sentence in English first."],
      ["Oral exams", "Organise an unfamiliar topic under light time pressure."],
      ["Daily life", "Tell a short story with a beginning, change and outcome."],
    ],
    focusHeading: "Turn careful French into usable French.",
    focusLead:
      "One unscripted take reveals different gaps from a written exercise—and gives you an immediate second try.",
    focusPoints: [
      [
        "Hold the sentence together",
        "Keep agreement and verb forms steady while attention stays on the message.",
      ],
      ["Use spoken connectors", "Guide the listener through a reason, contrast or consequence."],
      [
        "Hear the rhythm",
        "Use pronunciation feedback to compare liaison, sounds and phrasing across retries.",
      ],
    ],
    faqs: [
      [
        "Does the French pronunciation score use a French locale?",
        "Yes. Supported recordings are assessed with the fr-FR locale and the results appear separately from the main transcript-based report.",
      ],
      [
        "Will ChickyTutor correct every French mistake?",
        "The report prioritises the evidence it can support and the change most useful for a better next take; it is not an exhaustive correction list.",
      ],
    ],
  },
  {
    code: "nl",
    slug: "dutch",
    name: "Dutch",
    shortName: "Dutch",
    metaTitle: "Impromptu Dutch Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Practise speaking Dutch with 32 impromptu questions. Get a transcript, percentage scores, coaching and Dutch pronunciation feedback.",
    eyebrow: "Impromptu Dutch speaking practice",
    heading: "Practise the Dutch answer you cannot prepare.",
    lead: "Pick a Dutch topic, record one unrehearsed answer, and see exactly where your wording, structure or delivery needs another take.",
    storyHeading: "Knowing Dutch is different from joining in quickly.",
    storyBody:
      "The meeting moves on, the neighbour asks a follow-up, or the conversation changes direction. You need enough structure to answer before the moment disappears.",
    scenarios: [
      ["At work", "Give a concise view when a meeting moves off the agenda."],
      ["With neighbours", "Explain what happened without retreating to English."],
      ["Study", "Turn a broad question into a reasoned spoken answer."],
      ["Daily errands", "Describe a problem clearly when the exact word is missing."],
    ],
    focusHeading: "Make Dutch word order survive the pressure.",
    focusLead:
      "Practise complete thoughts while your attention is divided between the idea and the language.",
    focusPoints: [
      [
        "Place the verb",
        "Check what happens to word order when you add a reason or subordinate clause.",
      ],
      [
        "Choose the small words",
        "Notice articles, pronouns and prepositions that disappear in fast speech.",
      ],
      [
        "Keep the sound clear",
        "Compare Dutch vowels and consonants across two recordings of the same answer.",
      ],
    ],
    faqs: [
      [
        "Are the Dutch topics translated from English?",
        "The 32 prompts share reviewed speaking intents across languages, but the Dutch wording is stored locally and selected directly—no live translation is needed.",
      ],
      [
        "Can I practise for a Dutch speaking exam?",
        "You can use the timed prompts to practise organising and delivering an answer, but the scorecard is not an official exam assessment.",
      ],
    ],
  },
  {
    code: "de",
    slug: "german",
    name: "German",
    shortName: "German",
    metaTitle: "Impromptu German Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Answer 32 German speaking prompts without a script and get transcript-based scores, delivery coaching and pronunciation feedback.",
    eyebrow: "Impromptu German speaking practice",
    heading: "Build a clear German answer while you are speaking.",
    lead: "Choose an unfamiliar question, give one take in German, and use evidence from your own words to sharpen the next one.",
    storyHeading: "A prepared German sentence cannot answer every follow-up.",
    storyBody:
      "Spontaneous speaking asks you to hold the idea, the verb and the listener’s attention at the same time. Short focused takes make that pressure trainable.",
    scenarios: [
      ["Meetings", "Explain a priority or disagreement without losing the main clause."],
      ["Exams", "Develop a prompt into a position, reason and example."],
      ["Travel", "Describe what you need when the memorised phrase does not fit."],
      ["Conversation", "Tell a story with enough detail to invite the next question."],
    ],
    focusHeading: "Keep German structure intact when the thought grows.",
    focusLead:
      "The transcript shows where a spontaneous answer stayed controlled and where it became harder to follow.",
    focusPoints: [
      ["Anchor the verb", "Notice word order in main and subordinate clauses."],
      ["Name the relationship", "Use connectors to mark cause, contrast and sequence."],
      ["Shape the compounds", "Slow down enough to make longer words and endings audible."],
    ],
    faqs: [
      [
        "Which German variety is used for pronunciation?",
        "Pronunciation assessment uses the de-DE locale. Regional accents may produce different scores, so compare your own retries rather than treating one score as a verdict.",
      ],
      [
        "Do I need to speak for all 90 seconds?",
        "No. Ninety seconds is the ceiling; a shorter complete answer is better than padding the response.",
      ],
    ],
  },
  {
    code: "it",
    slug: "italian",
    name: "Italian",
    shortName: "Italian",
    metaTitle: "Impromptu Italian Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Practise 32 impromptu Italian topics and get a transcript, percentage scores, speaking feedback and Italian pronunciation assessment.",
    eyebrow: "Impromptu Italian speaking practice",
    heading: "Stay in Italian when the easy answer runs out.",
    lead: "Speak on an unexpected topic, see what ChickyTutor heard, and improve the same answer while the first take is still fresh.",
    storyHeading: "Conversation becomes real when you need to add your own detail.",
    storyBody:
      "Ordering and introductions are useful. Explaining a choice, telling a story and defending a view are what let you take part rather than recite.",
    scenarios: [
      ["Social conversation", "Move from a short reply to a personal example."],
      ["Travel", "Explain a change or problem in your own words."],
      ["Work and study", "Give a view with a reason and a useful conclusion."],
      ["Oral practice", "Respond to a broad prompt without writing first."],
    ],
    focusHeading: "Give your Italian room to move.",
    focusLead:
      "A one-take answer tests whether grammar and vocabulary are available when you need them, not only when you recognise them.",
    focusPoints: [
      ["Choose the tense", "Keep stories consistent as you move between background and events."],
      ["Make agreement audible", "Notice endings that blur when you speed up."],
      ["Connect naturally", "Use Italian links instead of translating English filler phrases."],
    ],
    faqs: [
      [
        "Can I create my own Italian topic?",
        "Yes. Write your own question or ask ChickyTutor to generate one for the subject and level you choose.",
      ],
      [
        "Does the report include Italian pronunciation?",
        "When the browser can prepare the audio, an it-IT pronunciation assessment is added to the report.",
      ],
    ],
  },
  {
    code: "pt",
    slug: "portuguese",
    name: "Brazilian Portuguese",
    shortName: "Portuguese",
    metaTitle: "Impromptu Portuguese Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Practise Brazilian Portuguese with 32 impromptu speaking questions, transcript-based percentage scores and pronunciation feedback.",
    eyebrow: "Impromptu Brazilian Portuguese practice",
    heading: "Turn a sudden question into clear Portuguese.",
    lead: "Choose a topic, answer without a script, and use the transcript, scorecard and pronunciation feedback to make the second take stronger.",
    storyHeading: "Spontaneous Portuguese begins where memorised dialogues stop.",
    storyBody:
      "The useful skill is staying with the thought when someone asks why, wants an example, or takes the conversation in a direction you did not expect.",
    scenarios: [
      ["Conversation", "Explain a preference and keep the exchange moving."],
      ["Travel", "Describe what changed when a set phrase is not enough."],
      ["Work", "Give a concise opinion and support it with one example."],
      ["Study", "Practise a structured oral response without notes."],
    ],
    focusHeading: "Practise Brazilian Portuguese as connected speech.",
    focusLead:
      "The report lets you compare the content and sound of your own retries without turning an accent into a proficiency grade.",
    focusPoints: [
      ["Carry the subject", "Keep person and verb forms aligned as the answer develops."],
      ["Link the clauses", "Show whether an idea is a reason, contrast or result."],
      [
        "Listen for reductions",
        "Compare clarity and rhythm in natural Brazilian Portuguese speech.",
      ],
    ],
    faqs: [
      [
        "Is this European or Brazilian Portuguese?",
        "The prompts and pronunciation locale use Brazilian Portuguese (pt-BR).",
      ],
      [
        "Can I still use it with a regional Brazilian accent?",
        "Yes. Pronunciation models may respond differently to regional accents, so the most useful comparison is between your own attempts.",
      ],
    ],
  },
  {
    code: "ja",
    slug: "japanese",
    name: "Japanese",
    shortName: "Japanese",
    metaTitle: "Impromptu Japanese Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Speak on 32 Japanese impromptu topics and get a segmented transcript, percentage scorecard, coaching and pronunciation feedback.",
    eyebrow: "Impromptu Japanese speaking practice",
    heading: "Build the Japanese response after the first phrase.",
    lead: "Answer a Japanese prompt in one take and see where the message stayed clear, where the structure slipped, and what to change next.",
    storyHeading: "A real Japanese answer has to adapt to the listener.",
    storyBody:
      "Introductions and set expressions create a start. Follow-up questions ask you to choose the detail, level of politeness and ending while you are already speaking.",
    scenarios: [
      ["Conversation", "Add detail without dropping back to a memorised exchange."],
      ["Work and study", "Explain your role or a decision with suitable politeness."],
      ["Oral exams", "Organise an unfamiliar topic into a complete response."],
      ["Daily life", "Tell a short event in an order the listener can follow."],
    ],
    focusHeading: "Practise Japanese in complete spoken thoughts.",
    focusLead:
      "Japanese is segmented into speech units for transcript evidence; ChickyTutor does not present a misleading whitespace-based WPM score.",
    focusPoints: [
      ["Land the ending", "Keep predicates and sentence endings consistent with the point."],
      ["Track the particles", "Notice the small markers that make relationships clear."],
      ["Match the register", "Choose forms that fit the person and situation you are imagining."],
    ],
    faqs: [
      [
        "Why is there no words-per-minute score for Japanese?",
        "Japanese does not separate words with spaces in the same way as English. ChickyTutor uses language-aware speech units for evidence and withholds a rate that would not be comparable.",
      ],
      [
        "Does Japanese pronunciation assessment work?",
        "Supported recordings use the ja-JP locale and show pronunciation measures separately from the main report.",
      ],
    ],
  },
  {
    code: "ko",
    slug: "korean",
    name: "Korean",
    shortName: "Korean",
    metaTitle: "Impromptu Korean Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Practise 32 Korean speaking topics without a script and get transcript-based percentage scores, coaching and pronunciation feedback.",
    eyebrow: "Impromptu Korean speaking practice",
    heading: "Keep the Korean sentence moving toward its ending.",
    lead: "Pick an unexpected topic, speak once, and use your transcript and scorecard to make the next answer more controlled.",
    storyHeading: "Spontaneous Korean asks you to decide the ending in real time.",
    storyBody:
      "The idea, the relationship with the listener and the final verb all shape the sentence. A short unrehearsed answer lets you practise those decisions together.",
    scenarios: [
      ["Conversation", "Respond beyond a set phrase when the topic changes."],
      ["Work and study", "Explain a view with an appropriate speech level."],
      ["Travel", "Describe a problem when the expected script no longer fits."],
      ["Oral practice", "Turn one question into a connected answer."],
    ],
    focusHeading: "Make the Korean ending support the message.",
    focusLead:
      "Use the transcript to see whether each thought reached a clear finish and the scorecard to choose one improvement.",
    focusPoints: [
      ["Choose the speech level", "Keep politeness consistent for the imagined listener."],
      ["Complete the thought", "Avoid stacking fragments before the final predicate."],
      ["Separate key sounds", "Compare consonants, vowels and rhythm across retries."],
    ],
    faqs: [
      [
        "Can beginners practise Korean here?",
        "Yes. Select First steps or Beginner and give a short complete answer using the words you have.",
      ],
      [
        "Which locale is used for Korean pronunciation?",
        "Supported recordings are assessed with ko-KR.",
      ],
    ],
  },
  {
    code: "zh",
    slug: "mandarin-chinese",
    name: "Mandarin Chinese",
    shortName: "Mandarin",
    metaTitle: "Impromptu Mandarin Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Practise 32 Mandarin Chinese speaking topics with a segmented transcript, percentage scores, coaching and pronunciation feedback.",
    eyebrow: "Impromptu Mandarin speaking practice",
    heading: "Say more than the Mandarin answer you rehearsed.",
    lead: "Choose a Chinese prompt, answer in one take, and see how clearly your meaning, structure and pronunciation came through.",
    storyHeading: "A memorised Mandarin phrase can start the exchange, not carry it.",
    storyBody:
      "When the listener asks why or wants an example, you need to assemble meaning quickly and keep going even if the exact word does not arrive.",
    scenarios: [
      ["Conversation", "Extend a reply with a reason and personal detail."],
      ["Work and study", "Explain a task or decision in a clear sequence."],
      ["Travel", "Describe the problem when a fixed phrase is too narrow."],
      ["Speaking tests", "Develop an unfamiliar prompt without writing the answer first."],
    ],
    focusHeading: "Practise Mandarin as meaning, rhythm and tone together.",
    focusLead:
      "Chinese transcript evidence uses language-aware speech units. ChickyTutor withholds a whitespace-based WPM comparison because it would be misleading.",
    focusPoints: [
      [
        "Set the time",
        "Use time words and aspect clearly rather than forcing English tense patterns.",
      ],
      ["Order the information", "Put context where the listener expects it before the main point."],
      [
        "Keep tones audible",
        "Use pronunciation measures to compare your own retries, including words worth another listen.",
      ],
    ],
    faqs: [
      [
        "Why does Mandarin show speech units instead of words per minute?",
        "Written Chinese does not separate every word with spaces. ChickyTutor segments the transcript for evidence but does not turn those units into an English-style WPM score.",
      ],
      [
        "Which Chinese variety does this page support?",
        "The prompts are in Simplified Chinese and pronunciation assessment uses the zh-CN Mandarin locale.",
      ],
    ],
  },
  {
    code: "hi",
    slug: "hindi",
    name: "Hindi",
    shortName: "Hindi",
    metaTitle: "Impromptu Hindi Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Answer 32 Hindi speaking prompts and get a transcript, percentage scores, delivery coaching and Hindi pronunciation feedback.",
    eyebrow: "Impromptu Hindi speaking practice",
    heading: "Practise a complete Hindi answer, not just the opening.",
    lead: "Choose a Hindi topic, speak without notes, and use the evidence in your report to make the second take clearer.",
    storyHeading: "The useful Hindi is the part you have to create yourself.",
    storyBody:
      "A new question tests whether you can hold the idea, choose natural word order and finish the thought without translating every line first.",
    scenarios: [
      ["Family and friends", "Tell a story with enough detail to keep the conversation going."],
      ["Work and study", "Explain a decision in a clear, respectful way."],
      ["Daily life", "Describe a routine, place or problem in your own words."],
      ["Speaking practice", "Build a reasoned answer to an unfamiliar question."],
    ],
    focusHeading: "Bring Hindi grammar into spontaneous speech.",
    focusLead:
      "The transcript makes small choices visible after the pressure of speaking has passed.",
    focusPoints: [
      ["Hold the agreement", "Watch gender, number and verb forms as the sentence grows."],
      [
        "Place the information",
        "Use Hindi word order instead of carrying an English sentence across.",
      ],
      [
        "Make contrasts clear",
        "Keep similar consonants and vowel length distinct in your retries.",
      ],
    ],
    faqs: [
      [
        "Does the prompt generator accept Devanagari?",
        "Yes. Fixed topics use Devanagari, and generated Hindi prompts may end with either Hindi or international sentence punctuation.",
      ],
      [
        "Is Hindi pronunciation included?",
        "When supported by the browser, pronunciation assessment uses the hi-IN locale.",
      ],
    ],
  },
  {
    code: "pl",
    slug: "polish",
    name: "Polish",
    shortName: "Polish",
    metaTitle: "Impromptu Polish Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Practise 32 impromptu Polish topics and receive a transcript, percentage scores, practical coaching and pronunciation feedback.",
    eyebrow: "Impromptu Polish speaking practice",
    heading: "Keep your Polish clear when the sentence changes shape.",
    lead: "Answer an unfamiliar Polish question in one take, then see which words, endings and ideas deserve a cleaner second try.",
    storyHeading: "Spontaneous Polish puts grammar and the message in the same moment.",
    storyBody:
      "You may recognise the right case in an exercise and still lose it while telling a story or defending a view. Speaking practice exposes that gap usefully.",
    scenarios: [
      ["Conversation", "Add a reason or example without shortening the thought."],
      ["Work", "Explain priorities and feedback in a form the listener can follow."],
      ["Daily life", "Describe a place, routine or change with concrete detail."],
      ["Oral practice", "Respond to a broad prompt without preparing sentences first."],
    ],
    focusHeading: "Test which Polish forms are ready for conversation.",
    focusLead:
      "Use each report as a narrow practice target, not a list of everything you could ever improve.",
    focusPoints: [
      [
        "Keep the case visible",
        "Notice endings that weaken when attention moves to the next idea.",
      ],
      ["Control the aspect", "Choose whether an action is ongoing, repeated or complete."],
      ["Shape consonant groups", "Compare difficult clusters at a natural speaking pace."],
    ],
    faqs: [
      [
        "Does ChickyTutor assess Polish pronunciation?",
        "Supported recordings use the pl-PL locale and return pronunciation measures alongside the main report.",
      ],
      [
        "Can I browse all Polish topics before recording?",
        "Yes. The page includes all 32 fixed Polish prompts, searchable by topic and category.",
      ],
    ],
  },
  {
    code: "ru",
    slug: "russian",
    name: "Russian",
    shortName: "Russian",
    metaTitle: "Impromptu Russian Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Speak on 32 Russian impromptu topics and get a transcript, percentage scorecard, actionable feedback and pronunciation assessment.",
    eyebrow: "Impromptu Russian speaking practice",
    heading: "Build the Russian answer while the thought is moving.",
    lead: "Pick an unexpected topic, record one answer, and use your transcript and scores to decide what the next take should fix.",
    storyHeading: "Spontaneous Russian reveals what passive knowledge can hide.",
    storyBody:
      "Cases, aspect and word stress all compete for attention when you are also trying to make a point. One short answer gives you evidence to work with.",
    scenarios: [
      ["Conversation", "Turn a brief response into a reason and an example."],
      ["Work and study", "Explain a task or disagreement without losing structure."],
      ["Travel", "Describe what happened when a memorised phrase is not enough."],
      ["Speaking tests", "Organise a broad topic into a complete response."],
    ],
    focusHeading: "Make Russian forms serve the point.",
    focusLead:
      "The aim is not to sound scripted. It is to keep the relationships between ideas clear while speaking naturally.",
    focusPoints: [
      ["Track the cases", "Notice whether endings still show who did what to whom."],
      ["Choose the aspect", "Make completed and ongoing actions match the story."],
      ["Hear the stress", "Use word-level feedback to revisit vowels and stressed syllables."],
    ],
    faqs: [
      [
        "Which Russian locale is used?",
        "Fixed prompts are in Russian and supported pronunciation assessment uses ru-RU.",
      ],
      [
        "Will the report penalise my accent?",
        "The main scores judge the transcript and message. Pronunciation is shown separately and is best used to compare your own attempts.",
      ],
    ],
  },
  {
    code: "sv",
    slug: "swedish",
    name: "Swedish",
    shortName: "Swedish",
    metaTitle: "Impromptu Swedish Speaking Practice & Topics | ChickyTutor",
    metaDescription:
      "Practise 32 Swedish speaking topics without notes and get transcript-based scores, coaching and Swedish pronunciation feedback.",
    eyebrow: "Impromptu Swedish speaking practice",
    heading: "Practise Swedish for the question after the small talk.",
    lead: "Choose a topic you did not rehearse, record one take, and turn the transcript into a more direct second answer.",
    storyHeading: "Comfortable Swedish grows in the unplanned part of the exchange.",
    storyBody:
      "It is one thing to introduce yourself. It is another to explain a trade-off, tell a useful story or respond when a colleague asks what you really think.",
    scenarios: [
      ["At work", "Give a direct view and support it without overexplaining."],
      ["Conversation", "Stay in Swedish when the subject moves beyond familiar ground."],
      ["Daily life", "Describe a choice, place or routine with useful detail."],
      ["Study", "Build a clear oral response to a new question."],
    ],
    focusHeading: "Make spontaneous Swedish sound connected.",
    focusLead:
      "The scorecard separates the message from language and delivery, so you can choose the next change instead of guessing.",
    focusPoints: [
      [
        "Keep V2 word order",
        "Notice what happens to the verb when another element starts the sentence.",
      ],
      [
        "Use natural links",
        "Mark reasons, contrasts and conclusions without translating word for word.",
      ],
      [
        "Compare the melody",
        "Use pronunciation feedback to revisit vowel length and sentence rhythm.",
      ],
    ],
    faqs: [
      [
        "Does Swedish pronunciation assessment work?",
        "Supported recordings use the sv-SE locale and show the available pronunciation measures separately.",
      ],
      [
        "Are there Swedish work topics?",
        "Yes. The 32-topic library includes work and study, opinion, story, explanation, persuasion, reflection, everyday life and imagination prompts.",
      ],
    ],
  },
];

export const IMPROMPTU_LANGUAGE_PAGES: ReadonlyArray<ImpromptuLanguagePage> = profiles.map(
  (profile) => ({ ...profile, path: `/impromptu/${profile.slug}` })
);

export function getImpromptuLanguagePage(slug: string) {
  return IMPROMPTU_LANGUAGE_PAGES.find((page) => page.slug === slug);
}

export function getImpromptuLanguagePageByCode(code: ImpromptuLanguageCode) {
  return IMPROMPTU_LANGUAGE_PAGES.find((page) => page.code === code);
}
